// ABOUTME: Standalone page for reviewing privacy policy and terms of service accessible via email links
// ABOUTME: Allows users to view and optionally accept updated policies without requiring login first

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../hooks/useImpersonation';
import { getPrivacyPolicy, getTermsOfService, acknowledgePolicyDocument } from '../services/api';
import type { PolicyDocument } from '../types/api';
import { renderPolicyMarkdown } from '../utils/sanitize';

const PolicyReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isImpersonating } = useImpersonation();

  const [policies, setPolicies] = useState<{
    privacy: PolicyDocument | null;
    terms: PolicyDocument | null;
  }>({
    privacy: null,
    terms: null
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const [readAcknowledgments, setReadAcknowledgments] = useState({
    privacy: false,
    terms: false
  });

  const loadPolicies = async () => {
    try {
      setLoading(true);
      setError(null);

      const [privacy, terms] = await Promise.all([
        getPrivacyPolicy(),
        getTermsOfService()
      ]);

      setPolicies({ privacy, terms });
    } catch {
      setError('Error loading policies. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, []);

  const renderMarkdown = renderPolicyMarkdown;

  const handleLoginRedirect = () => {
    const currentPath = encodeURIComponent('/policies/review');
    navigate(`/login?returnUrl=${currentPath}`);
  };

  const handleAccept = async () => {
    if (!user || !policies.privacy || !policies.terms) return;

    try {
      setAccepting(true);
      setError(null);

      // Accept both policies
      await Promise.all([
        acknowledgePolicyDocument({
          documentType: 'PrivacyPolicy',
          documentVersion: policies.privacy.version
        }),
        acknowledgePolicyDocument({
          documentType: 'TermsOfService',
          documentVersion: policies.terms.version
        })
      ]);

      setAccepted(true);

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch {
      setError('Error accepting policies. Please try again.');
      setAccepting(false);
    }
  };

  const handleRetry = () => {
    loadPolicies();
  };

  const canAccept = user && readAcknowledgments.privacy && readAcknowledgments.terms && !accepting && !accepted && !isImpersonating;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading policies...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !policies.privacy && !policies.terms) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading policies. Please try again.
        </Alert>
        <Button onClick={handleRetry} variant="contained">
          Try Again
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Policy Review
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Please review our updated policies below
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {accepted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Policies accepted successfully! Redirecting to dashboard...
        </Alert>
      )}

      {policies.privacy && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Privacy Policy
            {policies.privacy.versionLabel && (
              <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 2 }}>
                ({policies.privacy.versionLabel})
              </Typography>
            )}
          </Typography>

          <Paper sx={{ p: 3, mb: 3 }}>
            <div
              dangerouslySetInnerHTML={{
                __html: renderMarkdown(policies.privacy.content)
              }}
            />
          </Paper>

          {user && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={readAcknowledgments.privacy}
                  onChange={(e) => setReadAcknowledgments(prev => ({
                    ...prev,
                    privacy: e.target.checked
                  }))}
                />
              }
              label="I have read and understand the Privacy Policy"
            />
          )}
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      {policies.terms && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Terms of Service
            {policies.terms.versionLabel && (
              <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 2 }}>
                ({policies.terms.versionLabel})
              </Typography>
            )}
          </Typography>

          <Paper sx={{ p: 3, mb: 3 }}>
            <div
              dangerouslySetInnerHTML={{
                __html: renderMarkdown(policies.terms.content)
              }}
            />
          </Paper>

          {user && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={readAcknowledgments.terms}
                  onChange={(e) => setReadAcknowledgments(prev => ({
                    ...prev,
                    terms: e.target.checked
                  }))}
                />
              }
              label="I have read and understand the Terms of Service"
            />
          )}
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        {!user ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please log in to accept these policies and continue using Let&apos;s Reset.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleLoginRedirect}
            >
              Log In to Accept Policies
            </Button>
          </Box>
        ) : (
          <Tooltip title={isImpersonating ? "Cannot accept policies while impersonating" : ""}>
            <span>
              <Button
                variant="contained"
                size="large"
                onClick={handleAccept}
                disabled={!canAccept}
              >
                {accepting ? 'Accepting...' : accepted ? 'Policies Accepted!' : 'Accept Both Policies'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Container>
  );
};

export default PolicyReviewPage;